import { useIntl } from 'react-intl';
import { Controller, FieldValues, Path, useFormContext } from 'react-hook-form';
import { Autocomplete, AutocompleteOption, type AutocompleteProps } from '@atoms/inputs';

export type FormAutocompleteProps<T extends FieldValues, O extends AutocompleteOption> = Omit<
  AutocompleteProps<O>,
  'name'
> & {
  name: Path<T>;
  requiredMessage?: string;
  async?: boolean;
};
export function FormAutocomplete<O extends AutocompleteOption, T extends FieldValues>({
  name,
  requiredMessage,
  async = false,
  options = [],
  renderOption,
  ...props
}: FormAutocompleteProps<T, O>) {
  const intl = useIntl();
  const { required } = props;
  const { control } = useFormContext<T>();
  const rules = {
    ...(required
      ? {
          required: requiredMessage ?? intl.formatMessage({ id: 'forms.fieldIsRequired' }),
        }
      : {}),
  };

  return (
    <Controller<T>
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, ...field }, fieldState: { error } }) => {
        return (
          <Autocomplete
            {...props}
            options={
              options.map((opt, idx) => ({
                idx,
                value: opt.value,
                label: opt.label,
              })) as any
            }
            filterOptions={async ? (x) => x : undefined}
            onChange={(_, item) => {
              onChange(item?.value);
            }}
            renderOption={(props, option, state, ownerState) => {
              return renderOption ? renderOption(props, options[(option as any).idx], state, ownerState) : null;
            }}
            {...field}
            error={!!error}
            helperText={error?.message}
          />
        );
      }}
    />
  );
}
